import { useCallback, useMemo } from 'react';
import { Panel } from '../Panel.tsx';
import { Empty } from '../Empty.tsx';
import { duration, NO_VALUE } from '../format.ts';
import { STATUS_STATE } from '../state.ts';
import type { RobotStatus } from '../../hooks/useRobots.ts';
import { axisClock, ticks } from '../timeScale.ts';
import type { TimeAxis } from './channels.ts';
import { stampOf } from './channels.ts';
import { CursorOverlay } from './CursorOverlay.tsx';
import { CursorStamp } from './CursorStamp.tsx';
import { useCursor } from './cursor.ts';
import { frameOf } from './frame.ts';
import { usePlotSurface, xScale } from './plotSurface.ts';
import { runAt, valueRuns, type Run } from './scale.ts';
import type { ChartSeries } from './TimeSeriesCard.tsx';
import { useMeasure } from './useMeasure.ts';
import styles from './charts.module.css';

/* ==========================================================================
   The state strip — `state` as a categorical band, one lane per robot.

   §5's status-encoding hierarchy puts a ribbon segment second, above an edge
   bar and far above a badge, and `foxglove-state-transitions-timeline.png`
   is the reference: full-width bands with the value printed INSIDE the
   segment in mono, a graceful admission when the segments are too narrow to
   label, and one playhead shared with the traces above.

   A run ends when the value changes OR when the agent went quiet, so a band
   never spans a coverage hole — that would claim the robot held a mode
   through a period nobody heard from it.
   ========================================================================== */

/* --h-ribbon is 28px; a lane takes that when it is the only one and gives it
   back as the comparison grows, so four robots still fit above the grid. */
const LANE_H_ONE = 26;
const LANE_H_MANY = 22;
/** Below this a segment cannot carry its own name. */
const LABEL_MIN_PX = 52;
/** Below this the name fits but its dwell time does not. */
const DWELL_MIN_PX = 128;
/** Below this the lane is over-zoomed and says so. */
const HAIRLINE_PX = 2;

function toneOf(state: string | null): { tone: string; label: string } {
  if (state === null) return { tone: 'neutral', label: 'unknown' };
  const d = STATUS_STATE[state as RobotStatus];
  return d === undefined
    ? { tone: 'neutral', label: state }
    : { tone: d.tone, label: state };
}

export function StateStripCard({
  series,
  axis,
  field = 'state',
  title = 'Mode',
}: {
  series: readonly ChartSeries[];
  axis: TimeAxis;
  field?: 'state';
  title?: string;
}) {
  const cursor = useCursor();
  const [bodyRef, box] = useMeasure<HTMLDivElement>();

  const lanes = useMemo(
    () =>
      series.map((s) => {
        const t: number[] = [];
        const v: (string | null)[] = [];
        for (let i = s.readings.length - 1; i >= 0; i -= 1) {
          const r = s.readings[i]!;
          const ts = stampOf(r, axis);
          if (!Number.isFinite(ts) || ts < cursor.from || ts > cursor.to) continue;
          t.push(ts);
          v.push(r[field]);
        }
        const deltas: number[] = [];
        for (let i = 1; i < t.length; i += 1) deltas.push(t[i]! - t[i - 1]!);
        deltas.sort((a, b) => a - b);
        const cad = deltas.length > 0 ? deltas[deltas.length >> 1]! : 0;
        const gap = cad > 0 ? cad * 2.5 : Infinity;
        return {
          id: s.id,
          colour: s.colour,
          cadence: cad,
          /* The newest READING, not the last run's end — a run is extended by
             one cadence to cover the interval its final sample stands for,
             and stamping that would date the strip a cadence into the
             future. */
          last: t[t.length - 1] ?? null,
          runs: valueRuns(t, v, gap, cad) as Run<string | null>[],
        };
      }),
    [series, axis, cursor.from, cursor.to, field],
  );

  const cadence = lanes.find((l) => l.cadence > 0)?.cadence ?? 60_000;
  /* The strip shares the cards' gutters so its bands start on the same pixel
     as every trace above and below it — a mode ribbon that is 40px out of
     phase with the plot it explains is worse than no ribbon. */
  const frame = useMemo(() => frameOf(Math.max(1, box.w)), [box.w]);
  const surface = usePlotSurface({
    stepMs: cadence,
    padL: frame.padL,
    padR: frame.padR,
  });

  /* Stable composed ref — see useMeasure. */
  const { setBox } = surface;
  const attach = useCallback(
    (el: HTMLDivElement | null) => {
      bodyRef(el);
      setBox(el);
    },
    [bodyRef, setBox],
  );

  const total = lanes.reduce((n, l) => n + l.runs.length, 0);
  const w = box.w;
  /* With a gutter beside it a lane no longer needs a caption ROW: its name
     and the run under the cursor sit either side of the band, on the band's
     own line. */
  const LANE_H = lanes.length > 1 ? LANE_H_MANY : LANE_H_ONE;
  const h = Math.max(1, lanes.length * LANE_H);
  const x = xScale(cursor.from, cursor.to, frame.plotW);
  const xt = ticks(cursor.from, cursor.to, Math.max(2, Math.round(frame.plotW / 110)));

  const transitions = lanes.reduce((n, l) => n + Math.max(0, l.runs.length - 1), 0);
  /* Runs too thin to draw as anything but a hairline. Said in the caption,
     not laid over the band's last segment — a ribbon prints VALUES inside
     its segments (§5), and a note about the zoom is not one. */
  const overZoomed = lanes.some((l) =>
    l.runs.some(
      (r) => Math.min(frame.plotW, x(r.end)) - Math.max(0, x(r.start)) < HAIRLINE_PX,
    ),
  );

  /* WHAT INSTANT THE GRID UNDER THIS STRIP IS READING.

     Said ONCE, here, rather than on each of the nine cards below: they all
     draw the same window off the same cursor, and nine copies of one
     timestamp is the redundancy §13 tell 24 is against. The strip is the
     head of the comparison grid, so the stamp on it dates everything under
     it — and the ledger carries its own, because it is a different pane on
     a different scroll. */
  const newest = useMemo(() => {
    let m: number | null = null;
    for (const l of lanes) if (l.last !== null && (m === null || l.last > m)) m = l.last;
    return m;
  }, [lanes]);

  /* Resting, the strip quotes its NEWEST band rather than the window's right
     edge: a robot that stopped an hour ago still held a mode, and dropping to
     `—` while the rail beside it names that mode is the two-clocks bug in one
     panel. */
  const at = cursor.at ?? newest ?? cursor.to;
  const headRun =
    lanes.length === 1 ? runAt(lanes[0]!.runs, at, lanes[0]!.cadence) : null;

  return (
    <Panel
      title={title}
      {...(series.length > 1 ? { count: series.length } : {})}
      meta={
        <span className={styles.cardMeta} title="The agent's own `state` field, banded over time.">
          <span className={styles.metaUnit}>{field}</span>
          <span className={styles.metaRange}>{total}</span>
          <span className={styles.metaUnit}>{total === 1 ? 'run' : 'runs'}</span>
          <span className={styles.metaRange}>{transitions}</span>
          <span className={styles.metaUnit}>
            {transitions === 1 ? 'transition' : 'transitions'}
          </span>
          {lanes.length === 1 && (
            <span
              className={styles.metaValue}
              data-tone={headRun === null ? 'neutral' : toneOf(headRun.value).tone}
            >
              {headRun === null ? NO_VALUE : toneOf(headRun.value).label}
            </span>
          )}
          {overZoomed && <span className={styles.metaUnit}>zoom for more</span>}
          <CursorStamp at={cursor.at ?? newest} format={axisClock} />
        </span>
      }
      pad="none"
      scroll={false}
      className={`${styles.card} ${styles.stripCard}`}
    >
      {total === 0 ? (
        <Empty detail="mode · no readings in window">Nothing arrived in this window</Empty>
      ) : (
        <>
          <div
            className={styles.stripBody}
            style={{ height: `${h}px` }}
            ref={attach}
            role="group"
            aria-label={`Robot mode over time, ${lanes.length} ${
              lanes.length === 1 ? 'lane' : 'lanes'
            }. Arrow keys move the cursor.`}
            tabIndex={0}
            {...surface.handlers}
          >
            {w > 0 && (
              <svg width={w} height={h} aria-hidden="true">
                <line
                  x1={frame.x0 + 0.5} x2={frame.x0 + 0.5} y1={0} y2={h}
                  stroke="var(--line-2)" strokeWidth={1} shapeRendering="crispEdges"
                />
                <line
                  x1={frame.x1 + 0.5} x2={frame.x1 + 0.5} y1={0} y2={h}
                  stroke="var(--line-2)" strokeWidth={1} shapeRendering="crispEdges"
                />
                <g transform={`translate(${frame.x0},0)`}>
                  {xt.map((t) => (
                    <line
                      key={t.t}
                      x1={t.f * frame.plotW} x2={t.f * frame.plotW} y1={0} y2={h}
                      stroke="var(--plot-grid)" strokeWidth={1} shapeRendering="crispEdges"
                    />
                  ))}
                </g>
                {lanes.map((lane, li) => {
                  const y0 = li * LANE_H;
                  const active =
                    lane.runs.find((r) => at >= r.start && at <= r.end) ?? null;
                  return (
                    <g key={lane.id}>
                      {lanes.length > 1 && (
                        <>
                          <rect x={2} y={y0 + LANE_H / 2 - 5} width={7} height={2} fill={lane.colour} />
                          <text
                            className={styles.gutterName}
                            x={frame.nameX}
                            y={y0 + LANE_H / 2 + 3}
                          >
                            {lane.id}
                          </text>
                          <text
                            className={styles.gutterValue}
                            x={frame.valueX}
                            y={y0 + LANE_H / 2 + 3}
                            textAnchor="end"
                            data-tone={active === null ? 'neutral' : toneOf(active.value).tone}
                          >
                            {active === null ? NO_VALUE : toneOf(active.value).label}
                          </text>
                        </>
                      )}
                      <g transform={`translate(${frame.x0},0)`}>
                      <rect x={0} y={y0} width={frame.plotW} height={LANE_H} fill="var(--bg-0)" />
                      {lane.runs.map((r) => {
                        const a = Math.max(0, x(r.start));
                        const b = Math.min(frame.plotW, x(r.end));
                        const bw = Math.max(1, b - a);
                        const t = toneOf(r.value);
                        return (
                          <g key={`${r.start}-${String(r.value)}`} data-tone={t.tone}>
                            <rect
                              x={a} y={y0} width={bw} height={LANE_H}
                              fill="var(--tone)" fillOpacity={0.26}
                              shapeRendering="crispEdges"
                            />
                            <rect
                              x={a} y={y0 + LANE_H - 2} width={bw} height={2}
                              fill="var(--tone)"
                              shapeRendering="crispEdges"
                            />
                            {bw >= LABEL_MIN_PX && (
                              <text
                                className={styles.segLabel}
                                x={a + 5}
                                y={y0 + Math.round(LANE_H / 2) + 4}
                              >
                                {t.label}
                                {/* How long the robot HELD the mode, inside the
                                    segment that is already that long — the one
                                    number a ribbon cannot be read off by eye,
                                    because its width is a share of the window
                                    and not a duration. */}
                                {bw >= DWELL_MIN_PX && (
                                  <tspan className={styles.segDwell}>
                                    {`  ${duration(Math.min(r.end, cursor.to) - Math.max(r.start, cursor.from))}`}
                                  </tspan>
                                )}
                              </text>
                            )}
                          </g>
                        );
                      })}
                      </g>
                    </g>
                  );
                })}
                <g transform={`translate(${frame.x0},0)`}>
                  <CursorOverlay
                    from={cursor.from}
                    to={cursor.to}
                    width={frame.plotW}
                    height={h}
                    at={cursor.at}
                    playhead={cursor.playhead}
                    brush={surface.brush}
                  />
                </g>
              </svg>
            )}
          </div>
          <div className={styles.ruler}>
            {xt.map((t) => (
              <span
                key={t.t}
                className={styles.rulerTick}
                style={{ left: `${frame.x0 + t.f * frame.plotW}px` }}
              >
                {t.label}
              </span>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}
